/**
 * FALCON — THE CONTEXT ROLL, AS THE CODE ACTUALLY RUNS IT (2026-08-06).
 *
 * The board already carries the handover as a five-stage IDEA
 * (`falcon-platform.tsx`, loop A: MEASURE → ASK → SAVE → ROLL → CONTINUE).
 * That drawing is true and it stays. This one answers the next question down,
 * the one Erez asked after the 6-August outage: *what does the code do* — which
 * process evaluates what, which branch is taken, what is written where, and at
 * exactly which step it used to do the wrong thing.
 *
 * So these two are deliberately at a different altitude:
 *
 *   1 · THE ROLL, STEP BY STEP   the real path of one round-trip, from the
 *                                model turn to the fresh session — including
 *                                the branch tree inside `context_ceiling` and
 *                                the five session_state keys it turns on.
 *   2 · THE TWO FAULTS           what the same picture looked like before, and
 *                                why each fault ended in a client-visible
 *                                outage rather than a log line.
 *
 * WHY THE LABEL IS THE SUBJECT OF DIAGRAM 1. `PolicyEvent` carries no
 * conversation id — omnigent's `FunctionPolicy.evaluate` says the context param
 * is "not passed to the callable". But the engine loads `conversation_labels`
 * into `ctx.labels`, which reaches the callable as
 * `event["context"]["labels"]`. That label is the ONLY way a policy can know
 * whose conversation it is looking at, which is why it sits in the middle of
 * the diagram with everything downstream depending on it, and why a session
 * without one is drawn as a refusal in the accent colour. Position is the
 * argument.
 *
 * ⚠️ WHY EVERY STAGE IS A TOP-LEVEL COLUMN AND NOT A NESTED ROW.
 * `c4.tsx` paints the wire SVG first and the boxes over it, so any wire whose
 * whole path lies inside a container is hidden by that container's fill. The
 * numbered discs are the entire point of the data-flow archetype — "strip them
 * and it collapses into a pipeline" (skill://diagrams §4) — so a drawing that
 * hides them is not the archetype, it is a row of boxes. Wrapping the stages in
 * one titled band, which is what `falcon-platform.tsx` does, is exactly what
 * hides them: THE TWO LOOPS ON THIS BOARD HAS HAD INVISIBLE NUMBERED DISCS
 * SINCE 3 AUGUST for this reason. Keeping every stage a top-level column makes
 * each wire cross open canvas, so the numbers show. Fixing the paint order in
 * `c4.tsx` is the better cure and would fix the two loops as well, but it
 * restyles every diagram on every agent's board, so it needs Erez's word rather
 * than mine.
 *
 * All three are DATA objects on the house engine (`./c4`): nothing has a
 * hand-written width, and every wire resolves against a measured rect.
 */
import { Canvas, leaf, box, type Diagram } from "./c4";

// ═══ 1 · THE ROLL, STEP BY STEP ══════════════════════════════════════════════
//
// Left to right is the real order of one round-trip. The numbered discs are the
// content: strip them and this is a component list, not a flow.

const ROLL: Diagram = (() => {
  const turn = box("cr-turn", "1 · ONE ROUND-TRIP", "col", [
    leaf("cr-sdk", "claude-sdk", "[the model turn]", "Cpu"),
    leaf("cr-proxy", "runner proxy", "[PHASE_LLM_RESPONSE]", "Radio"),
  ], { gap: 12 });

  // The engine is drawn because two of its properties decide everything
  // downstream: it is what puts the labels on the event, and it is what turns a
  // raised exception into a DENY.
  const engine = box("cr-eng", "2 · THE POLICY ENGINE", "col", [
    leaf("cr-lbl", "inject labels", "[conversation_labels]", "Tags"),
    leaf("cr-disp", "dispatch", "[a raise becomes DENY]", "TriangleAlert"),
    leaf("cr-st", "session_state", "[5 keys, persisted]", "Database"),
  ], { gap: 12 });

  // The branch tree, in the order the code tests it. FLAT on purpose — a nested
  // band would hide every wire that starts inside it.
  const policy = box("cr-pol", "3 · context_ceiling", "col", [
    leaf("cr-tok", "read usage", "[context_tokens]", "Gauge"),
    leaf("cr-ask", "ASK", "[at the ceiling, once]", "Bell"),
    leaf("cr-reask", "REASK", "[round 3]", "RotateCw"),
    leaf("cr-roll", "ROLL", "[saved, or round 6]", "RefreshCw"),
    leaf("cr-saw", "saw the save", "[tool_call phase]", "PenLine"),
    leaf("cr-deny", "hard stop", "[request \u00b7 backstop]", "Ban"),
  ], { gap: 12 });

  // The middle of the diagram, and the whole of the fix.
  const gate = box("cr-gatebox", "4 · WHOSE CHAT IS THIS?", "col", [
    leaf("cr-chat", "read the label", "[falcon.chat_id]", "KeyRound"),
    leaf("cr-refuse", "NO LABEL — REFUSE", "[never the pointer]", "ShieldAlert", "gate"),
  ], { gap: 12 });

  const app = box("cr-app", "5 \u00b7 falcon-app", "col", [
    leaf("cr-say", "hidden message", "[is_meta \u00b7 into the live turn]", "EyeOff"),
    leaf("cr-new", "fresh session", "[POST /v1/sessions]", "Plus"),
    leaf("cr-stamp", "stamp the chat", "[PATCH labels]", "Tag"),
    leaf("cr-bind", "bind the chat", "[chat_sessions]", "Link"),
    leaf("cr-ptr", "move the pointer", "[OWNER ONLY]", "Compass"),
  ], { gap: 12 });

  const seed = box("cr-sd", "6 \u00b7 SEED \u2014 starts no turn", "col", [
    leaf("cr-obs", "append the brief", "[external_conversation_item]", "FileText"),
    leaf("cr-vis", "one visible line", "[external_assistant_message]", "MessageSquare"),
  ], { gap: 12 });

  const after = box("cr-out", "7 · AFTER", "col", [
    leaf("cr-old", "old session goes quiet", "[rolled exactly once]", "Moon"),
    leaf("cr-newc", "new session", "[zero turns]", "Sunrise"),
    leaf("cr-dan", "everyone else untouched", "[measured, not assumed]", "ShieldCheck"),
  ], { gap: 12 });

  return {
    key: "cr",
    title: "Falcon — the context roll, as the code runs it",
    subtitle: "One round-trip, end to end \u00b7 v1.1 \u00b7 verified by five tests, 2026-08-06",
    padBottom: 96,
    cols: [
      { root: turn, align: "center" },
      { root: engine, align: "center" },
      { root: policy, align: "center" },
      { root: gate, align: "center" },
      { root: app, align: "center" },
      { root: seed, align: "center" },
      { root: after, align: "center" },
    ],
    wires: [
      { a: "cr-proxy", b: "cr-lbl", as: "r", bs: "l", label: "HTTP", num: 1 },
      { a: "cr-lbl", b: "cr-tok", as: "r", bs: "l", label: "labels", num: 2 },
      { a: "cr-tok", b: "cr-ask", as: "r", bs: "l", num: 3 },
      { a: "cr-saw", b: "cr-roll", as: "r", bs: "r", dashed: true },
      { a: "cr-ask", b: "cr-chat", as: "r", bs: "l", num: 4 },
      { a: "cr-roll", b: "cr-chat", as: "r", bs: "l" },
      { a: "cr-chat", b: "cr-say", as: "r", bs: "l", num: 5 },
      { a: "cr-chat", b: "cr-new", as: "r", bs: "l", label: "chat_id", num: 6 },
      { a: "cr-refuse", b: "cr-app", as: "r", bs: "l", red: true },
      { a: "cr-ptr", b: "cr-obs", as: "r", bs: "l", num: 7 },
      { a: "cr-obs", b: "cr-newc", as: "r", bs: "l", num: 8 },
      { a: "cr-say", b: "cr-old", as: "r", bs: "l", dashed: true },
      { a: "cr-ptr", b: "cr-dan", as: "r", bs: "l", label: "owner only", red: true },
      { a: "cr-stamp", b: "cr-lbl", as: "t", bs: "t", dashed: true },
    ],
    legend: [
      { style: "solid", label: "ordered step \u2014 the numbers are the diagram" },
      { style: "dash", label: "sets state, or closes the loop" },
      { style: "red", label: "the only paths that can reach another person's chat" },
    ],
  };
})();

// ═══ 2 · FAULT A — IT ROLLED THE WRONG PERSON ════════════════════════
//
// The 6-August outage, in the same reading direction as diagram 1 so the two
// compare directly. It ends in what Dan SAW, because that is the only measure
// that matters: neither fault announced itself as a fault.

const step = (id: string, title: string, kids: Parameters<typeof box>[3]) =>
  ({ root: box(id, title, "col", kids, { gap: 12 }), align: "center" as const });

const FAULT_A: Diagram = {
  key: "fa",
  title: "Fault A — it rolled the wrong person",
  subtitle: "Shipped · ~15 minutes on 6 August, while Dan was working · fixed and regression-tested",
  padBottom: 96,
  cols: [
    step("fa-1", "ANY SESSION", [leaf("fa-any", "someone crosses", "[not Dan]", "UserRound")]),
    step("fa-2", "THE OLD LOOKUP", [leaf("fa-ptr", "_read_pointer", "[the ONE pointer]", "Compass")]),
    step("fa-3", "LANDED ON", [leaf("fa-dan", "Dan's conversation", "[live, mid-work]", "MessageSquare")]),
    step("fa-4", "THE SEED", [
      leaf("fa-msg", "posted a message", "[wakes the agent]", "Bell"),
      leaf("fa-turn", "a turn started", "[on its own brief]", "Cpu"),
    ]),
    step("fa-5", "WHAT DAN SAW", [
      leaf("fa-orph", "orphaned callbacks", "[no active turn context]", "Unplug"),
      leaf("fa-out", "could not read or save", "[~15 minutes]", "TriangleAlert", "gate"),
    ]),
  ],
  wires: [
    { a: "fa-any", b: "fa-ptr", as: "r", bs: "l", label: "crossed", num: 1 },
    { a: "fa-ptr", b: "fa-dan", as: "r", bs: "l", label: "wrong chat", num: 2, red: true },
    { a: "fa-dan", b: "fa-msg", as: "r", bs: "l", label: "handover", num: 3, red: true },
    { a: "fa-turn", b: "fa-orph", as: "r", bs: "l", label: "overlapped", num: 4, red: true },
    { a: "fa-orph", b: "fa-out", as: "b", bs: "t", red: true },
  ],
  legend: [
    { style: "solid", label: "ordered step" },
    { style: "red", label: "the path to a client-visible outage" },
  ],
};

// ═══ 3 · FAULT B — A MISSING ARGUMENT BECAME A PERMANENT DENIAL ══════════
//
// It never produced a symptom, because it only fires on the branch where the
// agent does NOT save — which the single 6-August test never reached, since the
// agent saved. Found by reading the code again rather than trusting the written
// account of it.

const FAULT_B: Diagram = {
  key: "fb",
  title: "Fault B — a missing argument became a permanent denial",
  subtitle: "Never fired · it needs the branch where the agent does not save · caught by re-reading, 6 August",
  padBottom: 96,
  cols: [
    step("fb-1", "THE BRANCH", [leaf("fb-r3", "round 3 reask", "[agent never saved]", "RotateCw")]),
    step("fb-2", "THE CALL", [leaf("fb-noarg", "_ask_handover()", "[no argument]", "CircleSlash")]),
    step("fb-3", "THE ENGINE", [
      leaf("fb-raise", "TypeError", "[a raise is fail-CLOSED]", "TriangleAlert"),
      leaf("fb-deny", "DENY", "[the response is dropped]", "Ban"),
    ]),
    step("fb-4", "THE TRAP", [leaf("fb-stuck", "state never returned", "[rounds stuck at 2]", "Lock")]),
    step("fb-5", "WHAT DAN WOULD SEE", [
      leaf("fb-forever", "every turn denied", "[no way back]", "TriangleAlert", "gate"),
    ]),
  ],
  wires: [
    { a: "fb-r3", b: "fb-noarg", as: "r", bs: "l", label: "signature", num: 1 },
    { a: "fb-noarg", b: "fb-raise", as: "r", bs: "l", label: "raises", num: 2, red: true },
    { a: "fb-raise", b: "fb-deny", as: "b", bs: "t" },
    { a: "fb-deny", b: "fb-stuck", as: "r", bs: "l", label: "no updates", num: 3, red: true },
    { a: "fb-stuck", b: "fb-forever", as: "r", bs: "l", label: "recomputes 3", num: 4, red: true },
  ],
  legend: [
    { style: "solid", label: "ordered step" },
    { style: "red", label: "the path to a client-visible outage" },
  ],
};

/** The roll as the code runs it — one round-trip, end to end. */
export function ContextRollDiagram() {
  return <Canvas d={ROLL} mode="contrast" />;
}

/** The two faults the current code replaces, drawn the same way for comparison. */
export function ContextFaultsDiagram() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 44 }}>
      <Canvas d={FAULT_A} mode="contrast" />
      <Canvas d={FAULT_B} mode="contrast" />
    </div>
  );
}
